import type { FC } from 'react'
import { useState } from 'react'

import { MessagePopup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Td } from '@skytech/ui/Table/Td'
import { useQuery } from '@tanstack/react-query'
import CloudDownloadOutlineIcon from 'mdi-react/CloudDownloadOutlineIcon'

import { Translation } from '@/app/translations/Translation'
import Highlight from '@/components/Highlight'
import { useFormattedNumberWithCurrency } from '@/hooks/use-formatted-number-with-currency'
import { useLocalDate } from '@/hooks/use-local-date'
import adminQueries from '@/modules/admin/queries'
import type { Invoice } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'

export const InvoicesListItem: FC<
  Invoice & { searchFilter: string; isAnyFileToDownload: boolean }
> = ({
  vendorName,
  id,
  invoiceNo,
  date,
  dueDate,
  invoiceAmount,
  availableForDownload,
  searchFilter,
  isAnyFileToDownload,
}) => {
  const { formatDate } = useLocalDate()
  const { formatNumber } = useFormattedNumberWithCurrency()

  const [isErrorPopupOpen, setIsErrorPopupOpen] = useState(false)

  const { refetch, isFetching } = useQuery({
    queryKey: adminQueries.adminRetrieveInvoicePdf(id.toString()),
    queryFn: () => adminResources.downloadInvoicePdf(id.toString()),
    enabled: false,
    retry: 0,
    onError: () => {
      setIsErrorPopupOpen(true)
    },
  })

  return (
    <tr tabIndex={0}>
      <Td>
        <Highlight pattern={searchFilter} text={vendorName} />
      </Td>
      <Td>
        <Highlight pattern={searchFilter} text={invoiceNo} />
      </Td>
      <Td className="hidden sm:table-cell">
        <Highlight pattern={searchFilter} text={formatDate({ date })} />
      </Td>
      <Td className="hidden sm:table-cell">
        <Highlight pattern={searchFilter} text={formatDate({ date: dueDate })} />
      </Td>
      <Td className="hidden pr-6 text-right sm:table-cell">
        <Highlight
          pattern={searchFilter}
          text={formatNumber({ amount: Number(invoiceAmount), precision: 2 })}
        />
      </Td>
      {isAnyFileToDownload && (
        <Td>
          {availableForDownload && (
            <Button
              Icon={<CloudDownloadOutlineIcon />}
              isLoading={isFetching}
              onClick={() => refetch()}
            />
          )}
        </Td>
      )}
      <MessagePopup
        title={<Translation id="File download error" />}
        message={
          <Translation id="We encountered a problem while downloading your file. Please refresh and try again." />
        }
        isOpen={isErrorPopupOpen}
        onClose={() => setIsErrorPopupOpen(false)}
      />
    </tr>
  )
}
